import { useRef, useCallback, useState } from 'react';

/**
 * LRU (Least Recently Used) cache hook
 * 
 * Maintains a cache with a maximum size. When the cache is full and a new item is added,
 * the least recently used item is evicted.
 * 
 * @param maxSize Maximum number of items to keep in the cache
 * @returns An object with get, set, has, clear, size, and keys methods
 */
export function useLRU<K, V>(maxSize: number) {
  // Use a Map to store key-value pairs
  // The order of entries in a Map is insertion order, so we can use this for LRU
  const cacheRef = useRef<Map<K, V>>(new Map());
  // Track cache version to trigger re-renders when cache changes
  const [cacheVersion, setCacheVersion] = useState(0);
  
  const updateVersion = useCallback(() => {
    setCacheVersion(prev => prev + 1);
  }, []);

  const get = useCallback((key: K): V | undefined => {
    const cache = cacheRef.current;
    if (!cache.has(key)) {
      return undefined;
    }
    
    // Move accessed item to end (most recently used)
    const value = cache.get(key)!;
    cache.delete(key);
    cache.set(key, value);
    
    return value;
  }, []);

  const set = useCallback((key: K, value: V): void => {
    const cache = cacheRef.current;
    
    // If key already exists, update it and move to end
    if (cache.has(key)) {
      cache.delete(key);
      cache.set(key, value);
      updateVersion();
      return;
    }
    
    // If cache is full, remove least recently used (first item)
    if (cache.size >= maxSize) {
      const firstKey = cache.keys().next().value;
      if (firstKey !== undefined) {
        cache.delete(firstKey);
      }
    }
    
    // Add new item (will be at the end)
    cache.set(key, value);
    updateVersion();
  }, [maxSize, updateVersion]);

  const has = useCallback((key: K): boolean => {
    return cacheRef.current.has(key);
  }, []);

  const clear = useCallback((): void => {
    cacheRef.current.clear();
    updateVersion();
  }, [updateVersion]);

  const size = useCallback((): number => {
    return cacheRef.current.size;
  }, []);

  const keys = useCallback((): K[] => {
    return Array.from(cacheRef.current.keys());
  }, []);

  return { get, set, has, clear, size, keys, cacheVersion };
}

